import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MetricCard } from "@/components/Reports/MetricCard";
import { FilterBar } from "@/components/Reports/FilterBar";
import { TrendChart } from "@/components/Reports/TrendChart";
import { HelpTooltip } from "@/components/HelpTooltip";
import { Gift, Users, TrendingUp, Award, BarChart, ChevronLeft, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';
import AppLayout from '@/layouts/AppLayout';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useState } from "react";

export default function RecognitionsReport({
  metrics = {},
  topSenders = [],
  topReceivers = [],
  trendData = [],
  hashtagDistribution = [],
  weekdayDistribution = [],
  pointsDistribution = [],
  topUsersByHashtag = {},
  recentRecognitions = [],
  filters = {}
}) {
  const [customDateRange, setCustomDateRange] = useState({
    start_date: filters.start_date || '',
    end_date: filters.end_date || ''
  });

  const handleBackToReports = () => {
    router.visit("/admin?tab=reports");
  };

  const handleApplyCustomDates = () => {
    if (customDateRange.start_date && customDateRange.end_date) {
      router.visit("/admin/reports/recognitions", {
        data: {
          range: 'custom',
          start_date: customDateRange.start_date,
          end_date: customDateRange.end_date,
          hashtag: filters.hashtag
        }
      });
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.range) params.append('range', filters.range);
      if (filters.hashtag) params.append('hashtag', filters.hashtag);

      const url = `/admin/reports/recognitions/export?${params.toString()}`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `recognitions_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al exportar:', error);
    }
  };

  const HASHTAG_COLORS = [
    '#10b981', // green
    '#3b82f6', // blue
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
    '#06b6d4'  // cyan
  ];

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToReports}
              className="text-purple-600 hover:bg-purple-50 -ml-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver a Reportes
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-bambu-black flex items-center gap-3">
            <Heart className="w-8 h-8 text-pink-600" />
            Reporte de Reconocimientos
            <HelpTooltip content={
              <div>
                <p className="font-semibold mb-1">¿Qué es este reporte?</p>
                <p className="mb-2">Analiza la actividad de reconocimientos en la plataforma, identificando tendencias, top performers y distribución de valores corporativos.</p>
                <p className="font-semibold mb-1">Verás:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Total de reconocimientos y puntos</li>
                  <li>Top emisores y receptores</li>
                  <li>Distribución de hashtags (valores)</li>
                  <li>Tendencias mensuales</li>
                  <li>Top 3 usuarios por valor corporativo</li>
                </ul>
              </div>
            } />
          </h1>
          <p className="text-gray-500 mt-1">Análisis de puntos compartidos entre empleados</p>
        </div>
        <Button
          onClick={handleExport}
          variant="outline"
          className="border-pink-200 text-pink-600 hover:bg-pink-50"
        >
          <Gift className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Rango de Fechas</label>
            <select
              value={filters.range || "last_30_days"}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  // Solo cambiar el selector, los inputs aparecerán abajo
                  router.visit("/admin/reports/recognitions", { data: { range: 'custom', hashtag: filters.hashtag } });
                } else {
                  router.visit("/admin/reports/recognitions", { data: { range: e.target.value, hashtag: filters.hashtag } });
                }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white"
            >
              <option value="last_7_days">Últimos 7 días</option>
              <option value="last_30_days">Últimos 30 días</option>
              <option value="last_90_days">Últimos 90 días</option>
              <option value="current_month">Mes actual</option>
              <option value="current_year">Año actual</option>
              <option value="custom">Rango personalizado...</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">Valor Corporativo</label>
            <select
              value={filters.hashtag || ""}
              onChange={(e) => router.visit("/admin/reports/recognitions", { data: { range: filters.range, start_date: filters.start_date, end_date: filters.end_date, hashtag: e.target.value || undefined } })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white"
            >
              <option value="">Todos los valores</option>
              {hashtagDistribution.map((item) => (
                <option key={item.hashtag} value={item.hashtag}>
                  {item.hashtag} ({item.count})
                </option>
              ))}
            </select>
          </div>

          {filters.hashtag && (
            <div className="flex items-end">
              <Button
                onClick={() => router.visit("/admin/reports/recognitions", { data: { range: filters.range, start_date: filters.start_date, end_date: filters.end_date } })}
                variant="outline"
                className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>

        {/* Rango de fechas personalizado */}
        {filters.range === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Inicio</label>
              <input
                type="date"
                value={customDateRange.start_date}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Fecha Fin</label>
              <input
                type="date"
                value={customDateRange.end_date}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleApplyCustomDates}
                disabled={!customDateRange.start_date || !customDateRange.end_date}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aplicar Fechas
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Reconocimientos"
          value={metrics.totalRecognitions || 0}
          icon={Heart}
          color="orange"
          trend={metrics.trends?.recognitionsChange}
          helpText="Cantidad total de reconocimientos otorgados en el período seleccionado"
        />
        <MetricCard
          title="Puntos Compartidos"
          value={metrics.totalPoints || 0}
          icon={Gift}
          color="green"
          suffix=" pts"
          trend={metrics.trends?.pointsChange}
          helpText="Suma total de puntos compartidos a través de reconocimientos"
        />
        <MetricCard
          title="Usuarios Activos"
          value={metrics.uniqueSenders || 0}
          icon={Users}
          color="blue"
          helpText="Número de usuarios únicos que han otorgado reconocimientos"
        />
        <MetricCard
          title="Participación"
          value={metrics.participationRate || 0}
          icon={TrendingUp}
          color="purple"
          suffix="%"
          helpText="Porcentaje de usuarios activos que participan en el programa de reconocimientos"
        />
      </div>

      {/* Evolución Temporal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Evolución Temporal
            <HelpTooltip content="Gráfico que muestra la tendencia de reconocimientos y puntos compartidos durante los últimos 6 meses, permitiendo identificar patrones estacionales y cambios en la actividad" />
          </CardTitle>
          <p className="text-sm text-gray-500">Tendencia de reconocimientos en los últimos 6 meses</p>
        </CardHeader>
        <CardContent>
          <TrendChart data={trendData} />
        </CardContent>
      </Card>

      {/* Distribución por Valor Corporativo y Día de la Semana */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Distribución por Valor Corporativo
              <HelpTooltip content="Muestra qué valores corporativos (hashtags) son más reconocidos en tu organización. Útil para identificar qué valores resuenan más con el equipo" />
            </CardTitle>
            <p className="text-sm text-gray-500">Hashtags más utilizados</p>
          </CardHeader>
          <CardContent>
            {hashtagDistribution.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                No hay datos para mostrar
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={hashtagDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ hashtag, percentage }) => `${hashtag}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {hashtagDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={HASHTAG_COLORS[index % HASHTAG_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Distribución por Día de la Semana
              <HelpTooltip content="Identifica qué días de la semana tienen mayor actividad de reconocimientos. Útil para planificar campañas de engagement" />
            </CardTitle>
            <p className="text-sm text-gray-500">Días con más actividad</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={weekdayDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="count" fill="#ec4899" radius={[8, 8, 0, 0]} name="Reconocimientos" />
              </RechartsBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Distribución por Rango de Puntos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Distribución por Rango de Puntos
            <HelpTooltip content="Muestra los rangos de puntos más comunes en los reconocimientos. Ayuda a entender si los usuarios tienden a dar reconocimientos de alto o bajo valor" />
          </CardTitle>
          <p className="text-sm text-gray-500">Cantidad de puntos por reconocimiento</p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={pointsDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="range" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} />
              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Cantidad" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Senders y Top Receivers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Top 10 Emisores
              <HelpTooltip content="Usuarios que más reconocimientos han otorgado. Identifica a los campeones de la cultura de reconocimiento en tu organización" />
            </CardTitle>
            <p className="text-sm text-gray-500">Usuarios que más reconocen a otros</p>
          </CardHeader>
          <CardContent>
            {topSenders.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No hay datos disponibles</div>
            ) : (
              <div className="space-y-2">
                {topSenders.map((user, index) => (
                  <div key={user.userId} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                        index === 0 ? "bg-yellow-100 text-yellow-700" :
                        index === 1 ? "bg-gray-100 text-gray-600" :
                        index === 2 ? "bg-orange-100 text-orange-700" :
                        "bg-blue-50 text-blue-600"
                      )}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 text-sm">{user.fullName}</div>
                        <div className="text-xs text-gray-500">{user.recognitionCount} reconocimientos</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-green-600 text-sm">{user.totalPoints} pts</div>
                      <div className="text-xs text-gray-400">otorgados</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              Top 10 Receptores
              <HelpTooltip content="Usuarios que más reconocimientos han recibido. Destaca a los miembros del equipo más valorados por sus compañeros" />
            </CardTitle>
            <p className="text-sm text-gray-500">Usuarios más reconocidos</p>
          </CardHeader>
          <CardContent>
            {topReceivers.length === 0 ? (
              <div className="text-center py-8 text-gray-400">No hay datos disponibles</div>
            ) : (
              <div className="space-y-2">
                {topReceivers.map((user, index) => (
                  <div key={user.userId} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                        index === 0 ? "bg-yellow-100 text-yellow-700" :
                        index === 1 ? "bg-gray-100 text-gray-600" :
                        index === 2 ? "bg-orange-100 text-orange-700" :
                        "bg-green-50 text-green-600"
                      )}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 text-sm">{user.fullName}</div>
                        <div className="text-xs text-gray-500">{user.recognitionCount} reconocimientos</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-green-600 text-sm">{user.totalPoints} pts</div>
                      <div className="text-xs text-gray-400">recibidos</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top 3 Usuarios por Valor Corporativo */}
      {Object.keys(topUsersByHashtag).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              Top 3 Usuarios por Valor Corporativo
              <HelpTooltip content="Identifica los 3 usuarios que más encarnan cada valor corporativo. Ideal para destacar campeones de valores específicos en comunicaciones internas o programas de reconocimiento especializados" />
            </CardTitle>
            <p className="text-sm text-gray-500">Usuarios que más reciben cada valor (#hashtag)</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(topUsersByHashtag).map(([hashtag, users]) => (
                <div key={hashtag} className="border border-gray-200 rounded-lg p-4 bg-linear-to-br from-white to-gray-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge className="bg-purple-100 text-purple-700 text-xs font-semibold">
                      {hashtag}
                    </Badge>
                  </div>
                  <div className="space-y-3">
                    {users.map((user, index) => (
                      <div key={user.user_id} className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-100 hover:border-purple-200 transition-colors">
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs",
                            index === 0 ? "bg-yellow-100 text-yellow-700" :
                            index === 1 ? "bg-gray-100 text-gray-600" :
                            "bg-orange-100 text-orange-700"
                          )}>
                            {index + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 text-xs truncate">{user.full_name}</div>
                            <div className="text-[10px] text-gray-500">{user.recognition_count} recibidos</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono font-bold text-purple-600 text-xs">{user.total_points}</div>
                          <div className="text-[10px] text-gray-400">pts</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reconocimientos Recientes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reconocimientos Recientes</CardTitle>
          <p className="text-sm text-gray-500">Últimos 50 reconocimientos en el período seleccionado</p>
        </CardHeader>
        <CardContent>
          {recentRecognitions.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No hay reconocimientos en el período seleccionado</div>
          ) : (
            <div className="space-y-3">
              {recentRecognitions.map((recognition) => (
                <div key={recognition.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500">
                        {new Date(recognition.created_at).toLocaleDateString()} {new Date(recognition.created_at).toLocaleTimeString()}
                      </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                      {recognition.hashtag}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <span className="font-medium text-gray-800">{recognition.sender?.full_name}</span>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium text-gray-800">{recognition.receiver?.full_name}</span>
                    <span className="ml-auto font-mono font-bold text-pink-600 text-xs">{recognition.points} pts</span>
                  </div>
                  {recognition.message && (
                    <p className="text-xs text-gray-600 mt-2 italic">"{recognition.message}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

RecognitionsReport.layout = page => <AppLayout children={page} />;
